import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { Note } from '../../../../../types/api';
import { getNoteTypeIcon, getNoteColor } from '../../../../../utils/noteIcons';
import { AttachmentList } from '../../maintenance/components/AttachmentList';
import { formatDateISO, formatDateTimeISO } from '../../../../../utils/dateUtils';
import { notePreview } from '../../../../../utils/noteText';

interface NoteCardProps {
  note: Note;
  onClick?: (note: Note) => void;
  highlighted?: boolean;
}

export const NoteCard: React.FC<NoteCardProps> = ({ note, onClick, highlighted = false }) => {
  const isResolved = !!note.resolved_at;

  const handleCardClick = () => {
    if (onClick) {
      onClick(note);
    }
  };

  return (
    <Card
      data-note-id={note.id}
      onClick={handleCardClick}
      sx={{
        mb: 2,
        opacity: isResolved ? 0.7 : 1,
        backgroundColor: highlighted ? '#e3f2fd' : isResolved ? '#f5f5f5' : 'white',
        border: highlighted
          ? '2px solid #2196F3'
          : isResolved
            ? '1px solid #e0e0e0'
            : '1px solid #ddd',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: highlighted ? '#bbdefb' : isResolved ? '#eeeeee' : '#f9f9f9',
          boxShadow: 2,
        },
      }}
    >
      <CardContent>
        {/* Header with type and priority */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {getNoteTypeIcon(note.note_type, note.priority, 'small')}
          <Chip
            label={note.note_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            size="small"
            sx={{ backgroundColor: getNoteColor(note.priority), color: 'white' }}
          />
          <Chip
            label={note.priority}
            size="small"
            color={note.priority === 'critical' ? 'error' : 'default'}
          />
          {isResolved && <Chip label="Resolved" size="small" color="success" variant="outlined" />}
        </Box>

        {/* Subject */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, overflowWrap: 'break-word' }}>
          {note.subject}
        </Typography>

        {/* Content preview — the full body renders as rich markdown in the
            note dialog; the list card shows a compact plain-text snippet so a
            long report can't dominate the list. */}
        <Typography
          variant="body1"
          paragraph
          color="text.secondary"
          sx={{ overflowWrap: 'break-word' }}
        >
          {notePreview(note.content)}
        </Typography>

        {/* Attachments */}
        {note.attachments && note.attachments.length > 0 && (
          <Box mb={2}>
            <AttachmentList attachments={note.attachments} compact />
          </Box>
        )}

        {/* Resolution info */}
        {isResolved && (
          <Box mb={2} p={2} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              Resolved {note.resolved_at && formatDateTimeISO(note.resolved_at)}
            </Typography>
            {note.resolution_notes && (
              <Typography variant="body2" sx={{ fontStyle: 'italic', overflowWrap: 'break-word' }}>
                {note.resolution_notes}
              </Typography>
            )}
          </Box>
        )}

        {/* Footer with metadata */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="caption" color="text.secondary">
              By: {note.author || 'Unknown'}
            </Typography>
            {note.date_of_action && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Date: {formatDateISO(note.date_of_action)}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Created: {formatDateTimeISO(note.created_at)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
